import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, Copy } from "@phosphor-icons/react";
import { api, errMsg, LANGS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHead, usePropertyList } from "@/pages/app/Dashboard";
import { useT } from "@/i18n";

export const PropertyPicker = ({ props, onPick }) => {
  const { t } = useT();
  return props.length > 0 && (
  <div><span className="label">{t("Fill from saved property")}</span>
    <select className="field" defaultValue="" onChange={(e) => onPick(props.find((p) => p.property_id === e.target.value))} data-testid="property-picker-select">
      <option value="" disabled>{t("Choose a property…")}</option>
      {props.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
    </select></div>
  );
};

export const useAiCall = () => {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const call = async (path, body) => {
    setBusy(true);
    try { const { data } = await api.post(path, body); await refresh(); return data; }
    catch (err) { toast.error(errMsg(err)); return null; }
    finally { setBusy(false); }
  };
  return [busy, call];
};

const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };

export default function Rewriter() {
  const { t, lang } = useT();
  const [props] = usePropertyList();
  const [f, setF] = useState({ facts: "", lang: lang === "it" ? "Italian" : "English", tone: "warm and practical" });
  const [out, setOut] = useState(null);
  const [busy, call] = useAiCall();

  const run = async (e) => {
    e.preventDefault();
    const d = await call("/ai/rewrite", f);
    if (d) setOut(d.output);
  };
  const full = out ? `${out.title}\n\n${(out.description || []).join("\n\n")}\n\n${(out.highlights || []).map((h) => `• ${h}`).join("\n")}` : "";

  return (
    <div data-testid="rewriter-page">
      <AppHead title="Listing rewriter" sub="Premium copy written by Claude. Only from the facts you give — no invented amenities." />
      <div className="grid xl:grid-cols-[420px_1fr] gap-6 items-start">
        <form onSubmit={run} className="card p-6 bg-white grid gap-3" data-testid="rewriter-form">
          <PropertyPicker props={props} onPick={(p) => p && setF({ ...f, facts: `${p.name}, ${p.property_type} in ${p.city}, ${p.country}.\n${p.facts}` })} />
          <div><span className="label">{t("Property facts")}</span><textarea required minLength={20} rows={9} className="field resize-none" value={f.facts} onChange={(e) => setF({ ...f, facts: e.target.value })} placeholder={t("Bedrooms, beds, views, walk times, Wi-Fi speed, what's nearby…")} data-testid="rewriter-facts-input" /></div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2 gap-3">
            <div><span className="label">{t("Language")}</span><select className="field" value={f.lang} onChange={(e) => setF({ ...f, lang: e.target.value })} data-testid="rewriter-lang-select">{LANGS.map((o) => <option key={o} value={o}>{t(o)}</option>)}</select></div>
            <div><span className="label">{t("Tone")}</span><select className="field" value={f.tone} onChange={(e) => setF({ ...f, tone: e.target.value })} data-testid="rewriter-tone-select">{["warm and practical", "elegant and calm", "fun and lively", "family-friendly", "business-ready"].map((o) => <option key={o} value={o}>{t(o)}</option>)}</select></div>
          </div>
          <button disabled={busy} className="btn-coral justify-center" data-testid="rewriter-submit-btn"><Sparkle size={18} weight="fill" /> {busy ? t("Writing your listing…") : t("Rewrite listing")}</button>
        </form>
        <div className="card p-8 bg-white min-h-[400px]" data-testid="rewriter-output">
          {busy && <div className="space-y-3">{[60, 95, 90, 80, 70].map((w) => <div key={w} className="skeleton h-4" style={{ width: `${w}%` }} />)}</div>}
          {!busy && !out && <p className="text-gray-500">{t("Your new listing will appear here.")}</p>}
          {!busy && out && (
            <div className="rise">
              <div className="flex justify-between gap-4 items-start">
                <h2 className="text-3xl text-[#0F4C5C]" data-testid="rewriter-title">{out.title}</h2>
                <button onClick={() => copy(full, t("Copied"))} className="btn-ghost text-sm !py-1.5 shrink-0" data-testid="rewriter-copy-btn"><Copy size={16} /> {t("Copy all")}</button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{out.title?.length} / 50 {t("characters")}</p>
              <div className="mt-6 space-y-4 text-gray-800 leading-relaxed" data-testid="rewriter-description">{(out.description || []).map((p, i) => <p key={i}>{p}</p>)}</div>
              <p className="label mt-8">{t("Highlights")}</p>
              <ul className="grid sm:grid-cols-2 gap-2" data-testid="rewriter-highlights">{(out.highlights || []).map((h, i) => <li key={i} className="rounded-lg bg-[#F4E9D8] px-3 py-2 text-sm">{h}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
